import * as THREE from 'three'

export type ProceduralStyle = 'round' | 'square' | 'aviator' | 'cateye'

export interface ProceduralGlassesOptions {
  style: ProceduralStyle
  /** Frame colour (hex). */
  color: number | string
  /** 'acetate' = plastic look, 'metal' = thin metallic rims. */
  material: 'acetate' | 'metal'
  lensTint: number | string
  lensOpacity: number
  /** Overall lens width (cm) */
  lensWidth: number
  lensHeight: number
  bridgeWidth: number
  templeLength: number
}

export const PROCEDURAL_DEFAULTS: ProceduralGlassesOptions = {
  style: 'square',
  color: 0x1c1c1c,
  material: 'acetate',
  lensTint: 0x88aacc,
  lensOpacity: 0.18,
  lensWidth: 5.0,
  lensHeight: 3.8,
  bridgeWidth: 1.8,
  templeLength: 13.5,
}

/** Node names used by the module (temple fit, lens material, variant overrides). */
export const NODE = {
  front: 'frame_front',
  lensL: 'lens_L',
  lensR: 'lens_R',
  templeL: 'temple_L',
  templeR: 'temple_R',
  bridge: 'bridge',
} as const

function lensShape(style: ProceduralStyle, w: number, h: number): THREE.Shape {
  const s = new THREE.Shape()
  const hw = w / 2, hh = h / 2
  switch (style) {
    case 'round': {
      s.absellipse(0, 0, hw, hh, 0, Math.PI * 2, false, 0)
      break
    }
    case 'aviator': {
      // teardrop: wide top, narrower bottom
      s.moveTo(-hw, hh * 0.55)
      s.bezierCurveTo(-hw, hh * 1.05, hw * 0.2, hh * 1.05, hw, hh * 0.6)
      s.bezierCurveTo(hw * 1.02, hh * 0.1, hw * 0.75, -hh, 0, -hh)
      s.bezierCurveTo(-hw * 0.9, -hh, -hw * 1.02, -hh * 0.3, -hw, hh * 0.55)
      break
    }
    case 'cateye': {
      s.moveTo(-hw, hh * 0.2)
      s.bezierCurveTo(-hw * 0.9, hh * 0.9, hw * 0.3, hh * 0.9, hw, hh * 1.05)
      s.bezierCurveTo(hw * 1.0, hh * 0.3, hw * 0.8, -hh, 0, -hh)
      s.bezierCurveTo(-hw * 0.9, -hh, -hw * 1.05, -hh * 0.4, -hw, hh * 0.2)
      break
    }
    case 'square':
    default: {
      const r = Math.min(hw, hh) * 0.35
      s.moveTo(-hw + r, hh)
      s.lineTo(hw - r, hh)
      s.quadraticCurveTo(hw, hh, hw, hh - r)
      s.lineTo(hw, -hh + r)
      s.quadraticCurveTo(hw, -hh, hw - r, -hh)
      s.lineTo(-hw + r, -hh)
      s.quadraticCurveTo(-hw, -hh, -hw, -hh + r)
      s.lineTo(-hw, hh - r)
      s.quadraticCurveTo(-hw, hh, -hw + r, hh)
    }
  }
  return s
}

function shapeToClosedCurve(shape: THREE.Shape, samples = 96): THREE.CatmullRomCurve3 {
  const pts2 = shape.getPoints(samples)
  const pts = pts2.map((p) => new THREE.Vector3(p.x, p.y, 0))
  if (pts.length > 1 && pts[0]!.distanceTo(pts[pts.length - 1]!) < 1e-4) pts.pop()
  return new THREE.CatmullRomCurve3(pts, true, 'catmullrom', 0.5)
}

/**
 * Lens material: tinted glass with subtle reflections. Pass the scene's environment texture as `envMap`
 * so `envMapIntensity` applies per-material (three ignores it for `scene.environment`).
 */
export function createLensMaterial(tint: number | string, opacity: number, envMap: THREE.Texture | null = null): THREE.MeshPhysicalMaterial {
  const m = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(tint),
    transparent: true,
    opacity,
    roughness: 0.08,
    metalness: 0,
    ior: 1.5,
    specularIntensity: 0.5,
    envMapIntensity: 0.25,
    clearcoat: 0.3,
    clearcoatRoughness: 0.05,
    side: THREE.DoubleSide,
    depthWrite: false,
  })
  m.envMap = envMap
  m.userData.arLens = true
  return m
}

export function createFrameMaterial(color: number | string, kind: 'acetate' | 'metal'): THREE.MeshStandardMaterial {
  return kind === 'metal'
    ? new THREE.MeshStandardMaterial({ color: new THREE.Color(color), metalness: 0.95, roughness: 0.28, envMapIntensity: 1.2 })
    : new THREE.MeshStandardMaterial({ color: new THREE.Color(color), metalness: 0.05, roughness: 0.32, envMapIntensity: 1.0 })
}

/**
 * Procedural spectacles in centimetres. Origin = centre of the bridge, back plane of the front.
 * +X → subject's left, +Y up, +Z out of the face. Temples pivot at their hinge and extend along −Z
 * with unit length (the module scales them to reach the ears).
 */
export function createProceduralGlasses(options: Partial<ProceduralGlassesOptions> = {}): THREE.Group {
  const o = { ...PROCEDURAL_DEFAULTS, ...options }
  const group = new THREE.Group()
  group.name = `procedural-glasses-${o.style}`

  const rimRadius = o.material === 'metal' ? 0.07 : 0.16
  const frameMat = createFrameMaterial(o.color, o.material)
  const lensMat = createLensMaterial(o.lensTint, o.lensOpacity)
  const front = new THREE.Group()
  front.name = NODE.front

  const shape = lensShape(o.style, o.lensWidth, o.lensHeight)
  const curve = shapeToClosedCurve(shape)
  const rimGeo = new THREE.TubeGeometry(curve, 160, rimRadius, 12, true)
  const lensGeo = new THREE.ShapeGeometry(shape, 24)
  const lensCenterX = o.bridgeWidth / 2 + o.lensWidth / 2

  for (const side of [1, -1] as const) {
    const rim = new THREE.Mesh(rimGeo, frameMat)
    rim.position.set(side * lensCenterX, 0, 0)
    rim.name = side > 0 ? 'rim_L' : 'rim_R'
    front.add(rim)
    const lens = new THREE.Mesh(lensGeo, lensMat)
    lens.position.set(side * lensCenterX, 0, -0.02)
    lens.name = side > 0 ? NODE.lensL : NODE.lensR
    lens.renderOrder = 10
    front.add(lens)
  }

  // bridge: arched tube between the two rims near the top
  const bx = o.bridgeWidth / 2 + rimRadius
  const by = o.lensHeight * 0.18
  const bridgeCurve = new THREE.QuadraticBezierCurve3(
    new THREE.Vector3(-bx, by, 0),
    new THREE.Vector3(0, by + 0.5, 0.15),
    new THREE.Vector3(bx, by, 0),
  )
  const bridge = new THREE.Mesh(new THREE.TubeGeometry(bridgeCurve, 16, rimRadius * 1.1, 10, false), frameMat)
  bridge.name = NODE.bridge
  front.add(bridge)

  // nose pads (small spheres slightly behind the front plane)
  const padGeo = new THREE.SphereGeometry(0.22, 12, 8)
  for (const side of [1, -1] as const) {
    const pad = new THREE.Mesh(padGeo, frameMat)
    pad.position.set(side * (o.bridgeWidth / 2 + 0.25), -0.55, -0.35)
    pad.name = side > 0 ? 'pad_L' : 'pad_R'
    front.add(pad)
  }

  // End pieces + temples. The hinge must sit ON the rim, and where that is depends on the lens shape:
  // square/cat-eye frames hinge near the top corner, round wire frames hinge at the horizontal midline.
  // Derive it from the outline: the outermost point of the shape (topmost such point along a flat side).
  const outline = shape.getPoints(96)
  let maxX = -Infinity
  for (const pt of outline) maxX = Math.max(maxX, pt.x)
  let hingeY = -Infinity
  for (const pt of outline) if (pt.x > maxX - rimRadius - 0.05) hingeY = Math.max(hingeY, pt.y)
  if (!Number.isFinite(hingeY)) hingeY = 0
  const rimOuterX = lensCenterX + maxX + rimRadius
  const endLen = o.material === 'metal' ? 0.4 : 0.55 // rim → hinge gap the end piece spans
  const hingeX = rimOuterX + endLen
  const templeGeo = new THREE.BoxGeometry(o.material === 'metal' ? 0.16 : 0.32, o.material === 'metal' ? 0.16 : 0.5, 1)
  templeGeo.translate(0, 0, -0.5) // extends from hinge along −Z, unit length
  const endGeo =
    o.material === 'metal'
      ? new THREE.BoxGeometry(endLen + 0.3, 0.26, 0.26) // slim lug, wire-frame look
      : new THREE.BoxGeometry(endLen + 0.3, 0.6, 0.5)
  for (const side of [1, -1] as const) {
    const endPiece = new THREE.Mesh(endGeo, frameMat)
    // overlaps the rim tube on one end and the temple pivot on the other — no floating gap
    endPiece.position.set(side * (rimOuterX + endLen / 2 - 0.15), hingeY, -0.08)
    endPiece.name = side > 0 ? 'endpiece_L' : 'endpiece_R'
    front.add(endPiece)

    const temple = new THREE.Group()
    temple.name = side > 0 ? NODE.templeL : NODE.templeR
    temple.position.set(side * hingeX, hingeY, -0.2)
    const arm = new THREE.Mesh(templeGeo, frameMat)
    arm.name = temple.name + '_arm'
    arm.scale.z = o.templeLength
    temple.add(arm)
    // ear tip (bends down)
    const tip = new THREE.Mesh(new THREE.CapsuleGeometry(0.16, 2.2, 4, 8), frameMat)
    tip.name = temple.name + '_tip'
    tip.position.set(0, -1.2, -o.templeLength - 0.2)
    tip.rotation.x = 0.35
    temple.add(tip)
    temple.userData.unitLengthArm = arm.name
    temple.userData.tip = tip.name
    group.add(temple)
  }
  group.add(front)
  return group
}
